import React, { useState, useEffect, useMemo } from 'react';
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Mail, Trophy, Zap, Target, Users, ChevronDown, ChevronUp, BarChart3 } from "lucide-react";
import { getNormTable, hasAnyNormData, getAgeBand } from "../data/normTables";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PublicLeaderboard = ({ onLogin }) => {
  const [allRecords, setAllRecords] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [showRecovery, setShowRecovery] = useState(false);
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState("ten_yard_sprint");
  const [aesLeaderboardData, setAesLeaderboardData] = useState([]);
  const [aesLeaderboardByAge, setAesLeaderboardByAge] = useState({});
  const [selectedTeam, setSelectedTeam] = useState(null); // For Team Stats tab
  useEffect(() => {
    fetchPublicRecords();
    fetchAESLeaderboard();
  }, []);

  const fetchAESLeaderboard = async () => {
    try {
      const [aesResponse, aesByAgeResponse] = await Promise.all([
        axios.get(`${API}/leaderboard/aes`),
        axios.get(`${API}/leaderboard/aes/by-age`)
      ]);
      
      setAesLeaderboardData(aesResponse.data.leaderboard || []);
      setAesLeaderboardByAge(aesByAgeResponse.data.leaderboards || {});
      console.log('AES Leaderboard loaded:', aesResponse.data.leaderboard?.length, 'athletes');
      console.log('Total qualified for AES:', aesResponse.data.total_qualified);
    } catch (error) {
      console.error("Failed to fetch AES leaderboard:", error);
    }
  };

  const fetchPublicRecords = async () => {
    try {
      const response = await axios.get(`${API}/athletes`);
      const athletesList = response.data;
      setAthletes(athletesList);
      
      let allRecordsData = [];
      for (const athlete of athletesList) {
        try {
          const recordsResponse = await axios.get(`${API}/records/athlete/${athlete.id}`);
          if (recordsResponse.data && recordsResponse.data.length > 0) {
            const athleteRecords = recordsResponse.data.map(record => ({
              ...record,
              athlete_name: athlete.name,
              athlete_age: record.athlete_age || athlete.age
            }));
            allRecordsData = allRecordsData.concat(athleteRecords);
          }
        } catch (error) {
          console.error(`Failed to fetch records for ${athlete.name}:`, error);
        }
      }
      console.log('Total records loaded:', allRecordsData.length);
      console.log('Records with all AES metrics:', allRecordsData.filter(r => 
        r.ten_yard_sprint !== null && r.ten_yard_sprint !== undefined &&
        r.broad_jump !== null && r.broad_jump !== undefined &&
        r.vertical_jump !== null && r.vertical_jump !== undefined
      ).length);
      setAllRecords(allRecordsData);
      setLoading(false);
    } catch (error) {
      console.error("Failed to fetch public records:", error);
      setLoading(false);
    }
  };

  const getLeaderboard = (metric, sortOrder = 'asc', sexFilter = null) => {
    const recordsWithMetric = allRecords.filter(r => r[metric] !== null && r[metric] !== undefined);
    
    const athleteBestMap = {};
    recordsWithMetric.forEach(record => {
      const athleteName = record.athlete_name;
      const athlete = athletes.find(a => a.name === athleteName);
      const athleteSex = athlete?.sex || null;
      
      // Apply sex filter if specified
      if (sexFilter && athleteSex !== sexFilter) return;
      
      if (!athleteBestMap[athleteName]) {
        athleteBestMap[athleteName] = {
          value: record[metric],
          date: record.date,
          athlete_name: athleteName,
          sex: athleteSex
        };
      } else {
        if (sortOrder === 'asc') {
          if (record[metric] < athleteBestMap[athleteName].value) {
            athleteBestMap[athleteName] = {
              value: record[metric],
              date: record.date,
              athlete_name: athleteName,
              sex: athleteSex
            };
          }
        } else {
          if (record[metric] > athleteBestMap[athleteName].value) {
            athleteBestMap[athleteName] = {
              value: record[metric],
              date: record.date,
              athlete_name: athleteName,
              sex: athleteSex
            };
          }
        }
      }
    });
    
    const leaderboard = Object.values(athleteBestMap);
    leaderboard.sort((a, b) => {
      if (sortOrder === 'asc') {
        return a.value - b.value;
      } else {
        return b.value - a.value;
      }
    });
    
    return leaderboard.slice(0, 10);
  };

  // Calculate Armory Explosiveness Score (AES)
  // AES = 50 * (2 / 10y) + 30 * (BJ / 60) + 20 * (VJ / 14)
  const calculateAES = (tenYardSprint, broadJump, verticalJump) => {
    if (tenYardSprint === null || tenYardSprint === undefined || 
        broadJump === null || broadJump === undefined || 
        verticalJump === null || verticalJump === undefined) {
      return null;
    }
    
    const speedComponent = 50 * (2 / tenYardSprint);
    const broadJumpComponent = 30 * (broadJump / 60);
    const verticalJumpComponent = 20 * (verticalJump / 14);
    
    const aes = speedComponent + broadJumpComponent + verticalJumpComponent;
    return Math.round(aes * 100) / 100;
  };

  // Get AES leaderboard (overall top 10) - memoized to recalculate when allRecords changes
  const aesLeaderboard = useMemo(() => {
    const athleteAESMap = {};
    
    allRecords.forEach(record => {
      const aes = calculateAES(record.ten_yard_sprint, record.broad_jump, record.vertical_jump);
      if (aes !== null) {
        const athleteName = record.athlete_name;
        const athlete = athletes.find(a => a.name === athleteName);
        if (!athleteAESMap[athleteName] || aes > athleteAESMap[athleteName].value) {
          athleteAESMap[athleteName] = {
            value: aes,
            athlete_name: athleteName,
            sex: athlete?.sex || null
          };
        }
      }
    });
    
    const leaderboard = Object.values(athleteAESMap);
    leaderboard.sort((a, b) => b.value - a.value);
    return leaderboard.slice(0, 10);
  }, [allRecords, athletes]);

  const getAESLeaderboard = () => aesLeaderboard;

  // Get AES leaderboard by age
  const getAESLeaderboardByAge = () => {
    const ageGroups = {};
    
    allRecords.forEach(record => {
      const aes = calculateAES(record.ten_yard_sprint, record.broad_jump, record.vertical_jump);
      if (aes !== null) {
        let age = record.athlete_age;
        const athlete = athletes.find(a => a.name === record.athlete_name);
        if (age === null || age === undefined) {
          if (athlete) {
            age = athlete.age;
          }
        }
        
        if (age !== null && age !== undefined) {
          if (!ageGroups[age]) {
            ageGroups[age] = {};
          }
          
          const athleteName = record.athlete_name;
          if (!ageGroups[age][athleteName] || aes > ageGroups[age][athleteName].value) {
            ageGroups[age][athleteName] = {
              value: aes,
              athlete_name: athleteName,
              athlete_age: age,
              sex: athlete?.sex || null
            };
          }
        }
      }
    });
    
    const ageLeaderboards = {};
    Object.keys(ageGroups).forEach(age => {
      const leaderboard = Object.values(ageGroups[age]);
      leaderboard.sort((a, b) => b.value - a.value);
      ageLeaderboards[age] = leaderboard.slice(0, 10);
    });
    
    return ageLeaderboards;
  };

  const getLeaderboardByAge = (metric, sortOrder = 'asc', sexFilter = null) => {
    const ageGroups = {};
    
    allRecords.forEach(record => {
      if (record[metric] !== null && record[metric] !== undefined) {
        // Use athlete_age from the record (age when record was set)
        // Fall back to current athlete age for backwards compatibility
        let age = record.athlete_age;
        const athlete = athletes.find(a => a.name === record.athlete_name);
        if (age === null || age === undefined) {
          if (athlete) {
            age = athlete.age;
          }
        }
        
        const athleteSex = athlete?.sex || null;
        
        // Apply sex filter if specified
        if (sexFilter && athleteSex !== sexFilter) return;
        
        if (age !== null && age !== undefined) {
          if (!ageGroups[age]) {
            ageGroups[age] = [];
          }
          ageGroups[age].push({
            athlete_name: record.athlete_name,
            value: record[metric],
            date: record.date,
            athlete_age: age,
            sex: athleteSex
          });
        }
      }
    });
    
    const ageLeaderboards = {};
    Object.keys(ageGroups).forEach(age => {
      const athleteBestMap = {};
      
      ageGroups[age].forEach(record => {
        const athleteName = record.athlete_name;
        if (!athleteBestMap[athleteName]) {
          athleteBestMap[athleteName] = record;
        } else {
          if (sortOrder === 'asc') {
            if (record.value < athleteBestMap[athleteName].value) {
              athleteBestMap[athleteName] = record;
            }
          } else {
            if (record.value > athleteBestMap[athleteName].value) {
              athleteBestMap[athleteName] = record;
            }
          }
        }
      });
      
      const leaderboard = Object.values(athleteBestMap);
      leaderboard.sort((a, b) => {
        if (sortOrder === 'asc') {
          return a.value - b.value;
        } else {
          return b.value - a.value;
        }
      });
      
      ageLeaderboards[age] = leaderboard.slice(0, 10);
    });
    
    return ageLeaderboards;
  };

  const handleLogin = (e) => {
    e.preventDefault();
    setLoginLoading(true);

    if (password === "Armory") {
      const expirationDate = new Date();
      expirationDate.setDate(expirationDate.getDate() + 365);
      
      localStorage.setItem("armory_authenticated", "true");
      localStorage.setItem("armory_auth_expiry", expirationDate.toISOString());
      
      toast.success("Welcome to The Armory!");
      onLogin();
    } else {
      toast.error("Password Incorrect");
      setPassword("");
    }
    
    setLoginLoading(false);
  };

  const handlePasswordRecovery = async () => {
    setRecoveryLoading(true);
    try {
      await axios.post(`${API}/auth/password-recovery`);
      toast.success("Password recovery email sent to kta.jason1@gmail.com");
      setShowRecovery(false);
    } catch (error) {
      toast.error("Failed to send recovery email");
    } finally {
      setRecoveryLoading(false);
    }
  };

  const metrics = [
    { key: 'ten_yard_sprint', label: '10 Yard Sprint', sortOrder: 'asc', unit: 's', color: 'purple' },
    { key: 'ten_yard_fly', label: '10 Yard Fly', sortOrder: 'asc', unit: 's', color: 'purple' },
    { key: 'agility_505', label: '505 Agility', sortOrder: 'asc', unit: 's', color: 'orange' },
    { key: 'broad_jump', label: 'Broad Jump', sortOrder: 'desc', unit: '"', color: 'blue' },
    { key: 'vertical_jump', label: 'Vertical Jump', sortOrder: 'desc', unit: '"', color: 'blue' },
    { key: 'aes', label: 'AES', sortOrder: 'desc', unit: '', color: 'amber', isAES: true }
  ];

  // AES Leaderboard Card Component - uses backend-calculated data
  const AESLeaderboardCard = ({ title, color, icon: Icon }) => {
    const data = aesLeaderboardData;
    const colorClasses = {
      amber: { bg: 'bg-gradient-to-r from-amber-50 to-orange-50', border: 'border-amber-500', title: 'text-amber-700', badge: 'text-amber-600' }
    };
    const classes = colorClasses[color] || colorClasses.amber;

    return (
      <Card className="bg-white/95 backdrop-blur shadow-lg hover:shadow-xl transition-shadow" data-testid="leaderboard-aes">
        <CardHeader className={`${classes.bg} border-b-2 ${classes.border} py-3`}>
          <CardTitle className={`${classes.title} text-lg flex items-center gap-2`}>
            <Icon size={20} />
            {title}
          </CardTitle>
          <CardDescription className="text-xs">Armory Explosive Score (10yd Sprint, BJ, VJ)</CardDescription>
        </CardHeader>
        <CardContent className="pt-3 pb-2">
          <div className="space-y-1">
            {data.length > 0 ? data.map((entry, index) => (
              <div 
                key={`aes-${index}`} 
                className={`flex justify-between items-center py-1.5 px-2 rounded text-sm ${index < 3 ? 'bg-amber-50' : ''}`}
              >
                <span className="flex items-center gap-1.5">
                  {index === 0 && <Trophy size={12} className="text-yellow-500" />}
                  {index === 1 && <Trophy size={12} className="text-gray-400" />}
                  {index === 2 && <Trophy size={12} className="text-amber-600" />}
                  <span className={`font-bold ${classes.badge}`}>{index + 1}.</span> 
                  <span className="truncate max-w-[130px]">
                    {entry.athlete_name}
                    {entry.sex && <span className="text-gray-500 ml-1">({entry.sex === 'male' ? 'M' : 'F'})</span>}
                  </span>
                </span>
                <span className={`font-bold ${classes.title} text-base`}>{entry.aes}</span>
              </div>
            )) : (
              <p className="text-sm text-gray-500 italic text-center py-4">No complete records yet</p>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  const LeaderboardCard = ({ title, metric, sortOrder, color, icon: Icon, unit }) => {
    const [showNorms, setShowNorms] = useState(false);
    const [selectedSex, setSelectedSex] = useState("");
    const [selectedAge, setSelectedAge] = useState("");
    const [leaderboardFilter, setLeaderboardFilter] = useState("all"); // "all", "male", "female"
    
    // Get data based on filter
    const sexFilter = leaderboardFilter === "all" ? null : leaderboardFilter;
    const data = getLeaderboard(metric, sortOrder, sexFilter);
    const hasNorms = hasAnyNormData(title);
    
    // Get norm data based on selected sex and age
    const normData = selectedSex && selectedAge 
      ? getNormTable(title, selectedSex, parseInt(selectedAge)) 
      : null;
    
    const ageBand = selectedAge ? getAgeBand(parseInt(selectedAge)) : null;
    
    const colorClasses = {
      purple: { bg: 'bg-purple-50', border: 'border-purple-500', title: 'text-purple-700', badge: 'text-purple-600' },
      blue: { bg: 'bg-blue-50', border: 'border-blue-500', title: 'text-blue-700', badge: 'text-blue-600' },
      green: { bg: 'bg-green-50', border: 'border-green-500', title: 'text-green-700', badge: 'text-green-600' },
      orange: { bg: 'bg-orange-50', border: 'border-orange-500', title: 'text-orange-700', badge: 'text-orange-600' }
    };
    const classes = colorClasses[color] || colorClasses.purple;

    return (
      <Card className="bg-white/95 backdrop-blur shadow-lg hover:shadow-xl transition-shadow" data-testid={`leaderboard-${metric}`}>
        <CardHeader className={`${classes.bg} border-b-2 ${classes.border} py-3`}>
          <CardTitle className={`${classes.title} text-lg flex items-center gap-2`}>
            <Icon size={20} />
            {title}
          </CardTitle>
          <CardDescription className="text-xs">Top 10 {sortOrder === 'asc' ? 'Fastest' : 'Best'}</CardDescription>
        </CardHeader>
        <CardContent className="pt-3 pb-2">
          {/* Sex Filter Tabs */}
          <div className="flex gap-1 mb-3 border-b border-gray-200 pb-2">
            <button
              onClick={() => setLeaderboardFilter("all")}
              className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                leaderboardFilter === "all" 
                  ? `${classes.bg} ${classes.title}` 
                  : "text-gray-500 hover:bg-gray-100"
              }`}
              data-testid={`filter-all-${metric}`}
            >
              Overall
            </button>
            <button
              onClick={() => setLeaderboardFilter("male")}
              className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                leaderboardFilter === "male" 
                  ? "bg-blue-100 text-blue-700" 
                  : "text-gray-500 hover:bg-gray-100"
              }`}
              data-testid={`filter-male-${metric}`}
            >
              Male
            </button>
            <button
              onClick={() => setLeaderboardFilter("female")}
              className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                leaderboardFilter === "female" 
                  ? "bg-pink-100 text-pink-700" 
                  : "text-gray-500 hover:bg-gray-100"
              }`}
              data-testid={`filter-female-${metric}`}
            >
              Female
            </button>
          </div>
          
          <div className="space-y-1">
            {data.length > 0 ? data.map((entry, index) => (
              <div 
                key={`${metric}-${leaderboardFilter}-${index}`} 
                className={`flex justify-between items-center py-1.5 px-2 rounded text-sm ${index < 3 ? 'bg-gray-50' : ''}`}
              >
                <span className="flex items-center gap-1.5">
                  {index === 0 && <Trophy size={12} className="text-yellow-500" />}
                  {index === 1 && <Trophy size={12} className="text-gray-400" />}
                  {index === 2 && <Trophy size={12} className="text-amber-600" />}
                  <span className={`font-bold ${classes.badge}`}>{index + 1}.</span> 
                  <span className="truncate max-w-[130px]">
                    {entry.athlete_name}
                    {entry.sex && <span className="text-gray-500 ml-1">({entry.sex === 'male' ? 'M' : 'F'})</span>}
                  </span>
                </span>
                <span className={`font-bold ${classes.title} text-base`}>{entry.value}{unit}</span>
              </div>
            )) : (
              <p className="text-sm text-gray-500 italic text-center py-4">
                {leaderboardFilter === "all" ? "No records yet" : `No ${leaderboardFilter} athletes with records`}
              </p>
            )}
          </div>
          
          {/* Norms Section */}
          {hasNorms && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <button
                onClick={() => setShowNorms(!showNorms)}
                className={`w-full flex items-center justify-between px-2 py-1.5 text-xs font-medium ${classes.title} hover:bg-gray-50 rounded transition-colors`}
                data-testid={`norms-toggle-${metric}`}
              >
                <span>Reference Norms</span>
                {showNorms ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
              
              {showNorms && (
                <div className="mt-2" data-testid={`norms-section-${metric}`}>
                  {/* Sex and Age Selectors */}
                  <div className="flex gap-2 mb-2">
                    <select
                      value={selectedSex}
                      onChange={(e) => setSelectedSex(e.target.value)}
                      className="flex-1 text-xs px-2 py-1.5 border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-purple-500"
                      data-testid={`norms-sex-select-${metric}`}
                    >
                      <option value="">Select Sex</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                    </select>
                    <select
                      value={selectedAge}
                      onChange={(e) => setSelectedAge(e.target.value)}
                      className="flex-1 text-xs px-2 py-1.5 border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-purple-500"
                      data-testid={`norms-age-select-${metric}`}
                    >
                      <option value="">Select Age</option>
                      {[6,7,8,9,10,11,12,13,14,15,16,17,18].map(age => (
                        <option key={age} value={age}>{age}</option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Message when sex not selected */}
                  {!selectedSex && (
                    <p className="text-xs text-gray-500 italic text-center py-2">
                      Set athlete sex to view norms.
                    </p>
                  )}
                  
                  {/* Message when age not selected */}
                  {selectedSex && !selectedAge && (
                    <p className="text-xs text-gray-500 italic text-center py-2">
                      Select age to view norms.
                    </p>
                  )}
                  
                  {/* Message when no norms for this combination */}
                  {selectedSex && selectedAge && !normData && (
                    <p className="text-xs text-gray-500 italic text-center py-2">
                      No norms available for {selectedSex} age {selectedAge} ({ageBand || 'out of range'}).
                    </p>
                  )}
                  
                  {/* Norm Table */}
                  {normData && (
                    <div className="overflow-x-auto" data-testid={`norms-table-${metric}`}>
                      <p className="text-xs text-gray-600 mb-1 font-medium">
                        {selectedSex === 'male' ? 'Male' : 'Female'} • Age {ageBand}
                      </p>
                      <table className="w-full text-xs">
                        <thead>
                          <tr className={`${classes.bg}`}>
                            <th className="px-2 py-1.5 text-left font-semibold text-gray-700">Category</th>
                            <th className="px-2 py-1.5 text-right font-semibold text-gray-700">Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {normData.map((row, idx) => (
                            <tr key={idx} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                              <td className={`px-2 py-1.5 font-medium ${
                                row.category === 'Advanced' ? 'text-green-700' :
                                row.category === 'On Track' ? 'text-blue-700' :
                                'text-orange-700'
                              }`}>{row.category}</td>
                              <td className="px-2 py-1.5 text-right text-gray-600">{row.value}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const AgeLeaderboardCard = ({ age, metric, sortOrder, unit, color, title }) => {
    const [showNormsDialog, setShowNormsDialog] = useState(false);
    const [leaderboardFilter, setLeaderboardFilter] = useState("all"); // "all", "male", "female"
    
    const sexFilter = leaderboardFilter === "all" ? null : leaderboardFilter;
    const ageData = getLeaderboardByAge(metric, sortOrder, sexFilter);
    const data = ageData[age] || [];
    
    // Check if norms exist for this assessment and age
    const ageBand = getAgeBand(parseInt(age));
    const hasNormsForMale = ageBand && getNormTable(title, 'male', parseInt(age));
    const hasNormsForFemale = ageBand && getNormTable(title, 'female', parseInt(age));
    const hasNormsForAge = hasNormsForMale || hasNormsForFemale;
    
    const colorClasses = {
      purple: { bg: 'bg-purple-50', border: 'border-purple-500', title: 'text-purple-700', badge: 'text-purple-600' },
      blue: { bg: 'bg-blue-50', border: 'border-blue-500', title: 'text-blue-700', badge: 'text-blue-600' },
      orange: { bg: 'bg-orange-50', border: 'border-orange-500', title: 'text-orange-700', badge: 'text-orange-600' }
    };
    const classes = colorClasses[color] || colorClasses.purple;

    // Check if there's any data for this age (in overall mode)
    const overallAgeData = getLeaderboardByAge(metric, sortOrder, null);
    if (!overallAgeData[age] || overallAgeData[age].length === 0) return null;

    return (
      <>
        <Card className="bg-white/95 backdrop-blur shadow-md" data-testid={`age-leaderboard-${age}`}>
          <CardHeader className={`${classes.bg} border-b-2 ${classes.border} py-2`}>
            <div className="flex items-center justify-between">
              <CardTitle className={`${classes.title} text-base flex items-center gap-2`}>
                <Users size={16} />
                Age {age}
              </CardTitle>
              {hasNormsForAge && (
                <button
                  onClick={() => setShowNormsDialog(true)}
                  className="text-xs px-2 py-1 bg-white/80 hover:bg-white rounded border border-gray-300 text-gray-600 hover:text-gray-800 transition-colors"
                  data-testid={`whats-good-btn-${age}`}
                >
                  What's Good?
                </button>
              )}
            </div>
          </CardHeader>
          <CardContent className="pt-2 pb-2">
            {/* Sex Filter Tabs */}
            <div className="flex gap-1 mb-2 border-b border-gray-200 pb-1">
              <button
                onClick={() => setLeaderboardFilter("all")}
                className={`px-1.5 py-0.5 text-[10px] font-medium rounded transition-colors ${
                  leaderboardFilter === "all" 
                    ? `${classes.bg} ${classes.title}` 
                    : "text-gray-500 hover:bg-gray-100"
                }`}
              >
                All
              </button>
              <button
                onClick={() => setLeaderboardFilter("male")}
                className={`px-1.5 py-0.5 text-[10px] font-medium rounded transition-colors ${
                  leaderboardFilter === "male" 
                    ? "bg-blue-100 text-blue-700" 
                    : "text-gray-500 hover:bg-gray-100"
                }`}
              >
                M
              </button>
              <button
                onClick={() => setLeaderboardFilter("female")}
                className={`px-1.5 py-0.5 text-[10px] font-medium rounded transition-colors ${
                  leaderboardFilter === "female" 
                    ? "bg-pink-100 text-pink-700" 
                    : "text-gray-500 hover:bg-gray-100"
                }`}
              >
                F
              </button>
            </div>
            
            <div className="space-y-1">
              {data.length > 0 ? data.map((entry, index) => (
                <div 
                  key={`age-${age}-${leaderboardFilter}-${index}`} 
                  className={`flex justify-between items-center py-1 px-2 rounded text-sm ${index < 3 ? 'bg-gray-50' : ''}`}
                >
                  <span className="flex items-center gap-1.5">
                    {index === 0 && <Trophy size={11} className="text-yellow-500" />}
                    {index === 1 && <Trophy size={11} className="text-gray-400" />}
                    {index === 2 && <Trophy size={11} className="text-amber-600" />}
                    <span className={`font-bold ${classes.badge}`}>{index + 1}.</span> 
                    <span className="truncate max-w-[70px]">
                      {entry.athlete_name}
                      {entry.sex && <span className="text-gray-500 ml-0.5">({entry.sex === 'male' ? 'M' : 'F'})</span>}
                    </span>
                  </span>
                  <span className={`font-bold ${classes.title}`}>{entry.value}{unit}</span>
                </div>
              )) : (
                <p className="text-xs text-gray-500 italic text-center py-2">
                  No {leaderboardFilter === "all" ? "" : leaderboardFilter} athletes
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* What's Good Dialog */}
        <Dialog open={showNormsDialog} onOpenChange={setShowNormsDialog}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className={`${classes.title}`}>
                {title} Norms - Age {ageBand}
              </DialogTitle>
              <DialogDescription>
                Reference standards for age group {ageBand}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {/* Male Norms */}
              {hasNormsForMale && (
                <div>
                  <h4 className="text-sm font-semibold text-blue-600 mb-2">Male</h4>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="px-2 py-1.5 text-left font-semibold text-gray-700">Category</th>
                        <th className="px-2 py-1.5 text-right font-semibold text-gray-700">Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getNormTable(title, 'male', parseInt(age)).map((row, idx) => (
                        <tr key={`m-${idx}`} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                          <td className={`px-2 py-1.5 font-medium ${
                            row.category === 'Advanced' ? 'text-green-700' :
                            row.category === 'On Track' ? 'text-blue-700' :
                            'text-orange-700'
                          }`}>{row.category}</td>
                          <td className="px-2 py-1.5 text-right text-gray-600">{row.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              
              {/* Female Norms */}
              {hasNormsForFemale && (
                <div>
                  <h4 className="text-sm font-semibold text-pink-600 mb-2">Female</h4>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="px-2 py-1.5 text-left font-semibold text-gray-700">Category</th>
                        <th className="px-2 py-1.5 text-right font-semibold text-gray-700">Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getNormTable(title, 'female', parseInt(age)).map((row, idx) => (
                        <tr key={`f-${idx}`} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                          <td className={`px-2 py-1.5 font-medium ${
                            row.category === 'Advanced' ? 'text-green-700' :
                            row.category === 'On Track' ? 'text-blue-700' :
                            'text-orange-700'
                          }`}>{row.category}</td>
                          <td className="px-2 py-1.5 text-right text-gray-600">{row.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  };

  const getAgesWithData = (metric, sortOrder) => {
    const ageData = getLeaderboardByAge(metric, sortOrder);
    return Object.keys(ageData).filter(age => ageData[age].length > 0).sort((a, b) => parseInt(a) - parseInt(b));
  };

  // Get unique teams from athletes
  const teams = useMemo(() => {
    const teamSet = new Set();
    athletes.forEach(a => {
      if (a.team) teamSet.add(a.team);
    });
    return Array.from(teamSet).sort();
  }, [athletes]);

  // Calculate team statistics for an assessment
  // IMPORTANT: Each athlete contributes only ONE value (their best result)
  // This matches the leaderboard logic - one athlete = one best result
  // Uses athlete ID for deduplication to handle same-name or name-change edge cases
  const getTeamStats = (teamName, metric, sortOrder) => {
    const teamAthletes = athletes.filter(a => a.team === teamName);
    // Create lookup maps for team athletes by both ID and name
    const teamAthleteById = {};
    const teamAthleteByName = {};
    teamAthletes.forEach(a => {
      if (a.id) teamAthleteById[a.id] = a;
      if (a.name) teamAthleteByName[a.name] = a;
    });
    
    // Get BEST value for each athlete on this team (one value per athlete)
    // Key by athlete ID for robustness, fall back to name for old records
    const athleteBestMap = {};
    allRecords.forEach(record => {
      if (record[metric] !== null && record[metric] !== undefined) {
        // Try to find the athlete - prefer ID match, fall back to name match
        let athlete = null;
        let dedupeKey = null;
        
        // First try to match by athlete_id from the record
        if (record.athlete_id && teamAthleteById[record.athlete_id]) {
          athlete = teamAthleteById[record.athlete_id];
          dedupeKey = athlete.id; // Use stable ID as key
        }
        // Fall back to name match for backward compatibility
        else if (record.athlete_name && teamAthleteByName[record.athlete_name]) {
          athlete = teamAthleteByName[record.athlete_name];
          dedupeKey = athlete.id || record.athlete_name; // Prefer ID, fall back to name
        }
        
        // Skip if athlete not on this team
        if (!athlete) return;
        
        if (!athleteBestMap[dedupeKey]) {
          // First record for this athlete
          athleteBestMap[dedupeKey] = {
            value: record[metric],
            sex: athlete.sex || null
          };
        } else {
          // Compare with existing best - keep better value
          if (sortOrder === 'asc') {
            // Lower is better (timed events)
            if (record[metric] < athleteBestMap[dedupeKey].value) {
              athleteBestMap[dedupeKey].value = record[metric];
            }
          } else {
            // Higher is better (jumps, etc.)
            if (record[metric] > athleteBestMap[dedupeKey].value) {
              athleteBestMap[dedupeKey].value = record[metric];
            }
          }
        }
      }
    });
    
    // Now athleteBestMap has exactly ONE entry per athlete (their best result)
    // N = number of unique athletes with valid results, NOT total record count
    const allValues = Object.values(athleteBestMap);
    const maleValues = allValues.filter(v => v.sex === 'male');
    const femaleValues = allValues.filter(v => v.sex === 'female');
    
    const calcStats = (values) => {
      if (values.length === 0) return null;
      const nums = values.map(v => v.value);
      const n = nums.length; // N = number of athletes, not records
      const mean = nums.reduce((a, b) => a + b, 0) / n;
      
      // Sample standard deviation (n-1 denominator)
      let sd = null;
      if (n > 1) {
        const variance = nums.reduce((acc, val) => acc + Math.pow(val - mean, 2), 0) / (n - 1);
        sd = Math.sqrt(variance);
      }
      
      return { n, mean, sd };
    };
    
    return {
      overall: calcStats(allValues),
      male: calcStats(maleValues),
      female: calcStats(femaleValues)
    };
  };

  // Assessment metrics config for team stats
  const assessmentConfigs = [
    { key: 'ten_yard_sprint', label: '10 Yard Sprint', unit: 's', sortOrder: 'asc', decimals: 2 },
    { key: 'ten_yard_fly', label: '10 Yard Fly', unit: 's', sortOrder: 'asc', decimals: 2 },
    { key: 'agility_505', label: '505 Agility', unit: 's', sortOrder: 'asc', decimals: 2 },
    { key: 'broad_jump', label: 'Broad Jump', unit: '"', sortOrder: 'desc', decimals: 1 },
    { key: 'vertical_jump', label: 'Vertical Jump', unit: '"', sortOrder: 'desc', decimals: 1 }
  ];

  // Team Performance Card Component (public view - no Coach Report)
  const TeamPerformanceCard = ({ teamName }) => {
    const [expanded, setExpanded] = useState(false);
    
    return (
      <Card className="bg-white/95 backdrop-blur shadow-md">
        <CardHeader 
          className="bg-slate-100 border-b border-slate-200 py-3 cursor-pointer hover:bg-slate-200 transition-colors"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex items-center justify-between">
            <CardTitle className="text-slate-700 text-base flex items-center gap-2">
              <Users size={18} />
              {teamName}
            </CardTitle>
            {expanded ? <ChevronUp size={18} className="text-slate-500" /> : <ChevronDown size={18} className="text-slate-500" />}
          </div>
        </CardHeader>
        
        {expanded && (
          <CardContent className="pt-4 pb-3">
            <div className="space-y-4">
              {assessmentConfigs.map(config => {
                const stats = getTeamStats(teamName, config.key, config.sortOrder);
                
                if (!stats.overall) return null;
                
                return (
                  <div key={config.key} className="border-b border-gray-100 pb-3 last:border-0 last:pb-0">
                    <h4 className="font-semibold text-sm text-slate-700 mb-2">{config.label}</h4>
                    
                    {/* Overall Stats */}
                    <div className="grid grid-cols-3 gap-2 text-xs mb-2">
                      <div className="bg-slate-50 rounded p-2 text-center">
                        <div className="text-slate-500 mb-0.5">N</div>
                        <div className="font-bold text-slate-700">{stats.overall.n}</div>
                      </div>
                      <div className="bg-slate-50 rounded p-2 text-center">
                        <div className="text-slate-500 mb-0.5">Mean</div>
                        <div className="font-bold text-slate-700">
                          {stats.overall.mean.toFixed(config.decimals)}{config.unit}
                        </div>
                      </div>
                      <div className="bg-slate-50 rounded p-2 text-center">
                        <div className="text-slate-500 mb-0.5">SD</div>
                        <div className="font-bold text-slate-700">
                          {stats.overall.sd !== null ? stats.overall.sd.toFixed(config.decimals) : '—'}
                        </div>
                      </div>
                    </div>
                    
                    {/* Sex Breakdown */}
                    <div className="flex gap-2">
                      {stats.male && (
                        <div className="flex-1 bg-blue-50 rounded p-2">
                          <div className="text-[10px] font-semibold text-blue-600 mb-1">Male (N={stats.male.n})</div>
                          <div className="text-xs text-blue-700">
                            <span className="font-medium">μ:</span> {stats.male.mean.toFixed(config.decimals)}{config.unit}
                            {stats.male.sd !== null && (
                              <span className="ml-2"><span className="font-medium">σ:</span> {stats.male.sd.toFixed(config.decimals)}</span>
                            )}
                          </div>
                        </div>
                      )}
                      {stats.female && (
                        <div className="flex-1 bg-pink-50 rounded p-2">
                          <div className="text-[10px] font-semibold text-pink-600 mb-1">Female (N={stats.female.n})</div>
                          <div className="text-xs text-pink-700">
                            <span className="font-medium">μ:</span> {stats.female.mean.toFixed(config.decimals)}{config.unit}
                            {stats.female.sd !== null && (
                              <span className="ml-2"><span className="font-medium">σ:</span> {stats.female.sd.toFixed(config.decimals)}</span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        )}
      </Card>
    );
  };

  const currentMetricConfig = metrics.find(m => m.key === selectedMetric) || metrics[0];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white text-xl">Loading records...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Hero Header with Logo Background */}
      <div className="relative overflow-hidden">
        {/* Full-width zoomed logo background */}
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_5a5bf1d9-b469-45cf-bf84-0c362d7a4119/artifacts/zhg878ta_497812030_122128933850790773_5395653852145838309_n%20%281%29.jpg)',
            backgroundPosition: 'center 30%',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            filter: 'brightness(0.6)',
          }}
        />
        {/* Dark gradient overlay for readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/70 via-slate-900/50 to-slate-900/80" />
        
        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 py-12 md:py-16 flex flex-col items-center">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2 text-center drop-shadow-lg" style={{ fontFamily: 'Space Grotesk, sans-serif' }} data-testid="public-title">
            Top Performers
          </h1>
          <p className="text-base md:text-lg text-white/90 drop-shadow">The Armory - Strength & Speed Leaderboards</p>
        </div>
      </div>

      {/* Tabs for Overall vs By Age vs Team Performance */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <Tabs defaultValue="overall" className="w-full">
          <TabsList className="grid w-full max-w-lg mx-auto grid-cols-3 mb-6" data-testid="leaderboard-tabs">
            <TabsTrigger value="overall" data-testid="overall-tab">Overall Top 10</TabsTrigger>
            <TabsTrigger value="byage" data-testid="byage-tab">Top 10 by Age</TabsTrigger>
            <TabsTrigger value="teams" data-testid="teams-tab">Team Stats</TabsTrigger>
          </TabsList>

          {/* Overall Top 10 Tab */}
          <TabsContent value="overall">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="leaderboards-grid">
              <LeaderboardCard 
                title="10 Yard Sprint" 
                metric="ten_yard_sprint" 
                sortOrder="asc" 
                color="purple"
                icon={Zap}
                unit="s"
              />
              <LeaderboardCard 
                title="10 Yard Fly" 
                metric="ten_yard_fly" 
                sortOrder="asc" 
                color="purple"
                icon={Zap}
                unit="s"
              />
              <LeaderboardCard 
                title="505 Agility" 
                metric="agility_505" 
                sortOrder="asc" 
                color="orange"
                icon={Target}
                unit="s"
              />
              <LeaderboardCard 
                title="Broad Jump" 
                metric="broad_jump" 
                sortOrder="desc" 
                color="blue"
                icon={Zap}
                unit='"'
              />
              <LeaderboardCard 
                title="Vertical Jump" 
                metric="vertical_jump" 
                sortOrder="desc" 
                color="blue"
                icon={Zap}
                unit='"'
              />
              <AESLeaderboardCard 
                title="AES Score" 
                color="amber"
                icon={Zap}
              />
            </div>
          </TabsContent>

          {/* By Age Tab */}
          <TabsContent value="byage">
            {/* Metric Selector */}
            <div className="flex flex-wrap justify-center gap-2 mb-6" data-testid="metric-selector">
              {metrics.map(metric => (
                <Button
                  key={metric.key}
                  variant={selectedMetric === metric.key ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedMetric(metric.key)}
                  className={selectedMetric === metric.key ? "bg-slate-700" : "bg-white/90"}
                  data-testid={`metric-btn-${metric.key}`}
                >
                  {metric.label}
                </Button>
              ))}
            </div>

            {/* Age Groups Grid */}
            {currentMetricConfig.isAES ? (
              // AES By Age Display - uses backend data
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-testid="age-leaderboards-grid">
                {Object.entries(aesLeaderboardByAge)
                  .sort((a, b) => parseInt(a[0]) - parseInt(b[0]))
                  .map(([age, athletes]) => (
                  <Card key={`aes-age-${age}`} className="bg-white/95 backdrop-blur shadow-md" data-testid={`age-leaderboard-${age}`}>
                    <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50 border-b-2 border-amber-500 py-2">
                      <CardTitle className="text-amber-700 text-base flex items-center gap-2">
                        <Users size={16} />
                        Age {age}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-2 pb-2">
                      <div className="space-y-1">
                        {athletes.map((entry, index) => (
                          <div 
                            key={`aes-${age}-${index}`} 
                            className={`flex justify-between items-center py-1 px-2 rounded text-sm ${index < 3 ? 'bg-amber-50' : ''}`}
                          >
                            <span className="flex items-center gap-1.5">
                              {index === 0 && <Trophy size={11} className="text-yellow-500" />}
                              {index === 1 && <Trophy size={11} className="text-gray-400" />}
                              {index === 2 && <Trophy size={11} className="text-amber-600" />}
                              <span className="font-bold text-amber-600">{index + 1}.</span> 
                              <span className="truncate max-w-[70px]">
                                {entry.athlete_name}
                                {entry.sex && <span className="text-gray-500 ml-0.5">({entry.sex === 'male' ? 'M' : 'F'})</span>}
                              </span>
                            </span>
                            <span className="font-bold text-amber-700">{entry.aes}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              // Regular metrics By Age Display
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-testid="age-leaderboards-grid">
                {getAgesWithData(currentMetricConfig.key, currentMetricConfig.sortOrder).map(age => (
                  <AgeLeaderboardCard
                    key={`age-card-${age}`}
                    age={age}
                    metric={currentMetricConfig.key}
                    sortOrder={currentMetricConfig.sortOrder}
                    unit={currentMetricConfig.unit}
                    color={currentMetricConfig.color}
                    title={currentMetricConfig.label}
                  />
                ))}
              </div>
            )}

            {!currentMetricConfig.isAES && getAgesWithData(currentMetricConfig.key, currentMetricConfig.sortOrder).length === 0 && (
              <div className="text-center py-12">
                <p className="text-white/70 text-lg">No records yet for {currentMetricConfig.label}</p>
              </div>
            )}
            {currentMetricConfig.isAES && Object.keys(aesLeaderboardByAge).length === 0 && (
              <div className="text-center py-12">
                <p className="text-white/70 text-lg">No complete AES records yet (requires 10Y Sprint, Broad Jump, and Vertical Jump)</p>
              </div>
            )}
          </TabsContent>

          {/* Team Stats Tab */}
          <TabsContent value="teams">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-6">
                <h2 className="text-xl font-semibold text-white mb-2 flex items-center justify-center gap-2">
                  <BarChart3 size={24} />
                  Team Performance Statistics
                </h2>
                <p className="text-white/70 text-sm">Select a team to view statistics</p>
              </div>
              
              {teams.length > 0 ? (
                <>
                  {/* Team Selection Buttons */}
                  <div className="flex flex-wrap justify-center gap-2 mb-6">
                    {teams.map(team => (
                      <button
                        key={team}
                        onClick={() => setSelectedTeam(selectedTeam === team ? null : team)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          selectedTeam === team
                            ? 'bg-slate-700 text-white'
                            : 'bg-white/90 text-slate-700 hover:bg-white'
                        }`}
                      >
                        {team}
                      </button>
                    ))}
                  </div>
                  
                  {/* Selected Team Stats Panel */}
                  {selectedTeam && (
                    <Card className="bg-white/95 backdrop-blur shadow-lg">
                      <CardHeader className="bg-slate-100 border-b border-slate-200 py-4">
                        <CardTitle className="text-slate-700 text-lg flex items-center gap-2">
                          <Users size={20} />
                          {selectedTeam} – Team Performance Statistics
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-4 pb-4">
                        <div className="space-y-4">
                          {assessmentConfigs.map(config => {
                            const stats = getTeamStats(selectedTeam, config.key, config.sortOrder);
                            
                            if (!stats.overall) return null;
                            
                            return (
                              <div key={config.key} className="border-b border-gray-100 pb-3 last:border-0 last:pb-0">
                                <h4 className="font-semibold text-sm text-slate-700 mb-2">{config.label}</h4>
                                
                                {/* Overall Stats */}
                                <div className="grid grid-cols-3 gap-2 text-xs mb-2">
                                  <div className="bg-slate-50 rounded p-2 text-center">
                                    <div className="text-slate-500 mb-0.5">N</div>
                                    <div className="font-bold text-slate-700">{stats.overall.n}</div>
                                  </div>
                                  <div className="bg-slate-50 rounded p-2 text-center">
                                    <div className="text-slate-500 mb-0.5">Mean</div>
                                    <div className="font-bold text-slate-700">
                                      {stats.overall.mean.toFixed(config.decimals)}{config.unit}
                                    </div>
                                  </div>
                                  <div className="bg-slate-50 rounded p-2 text-center">
                                    <div className="text-slate-500 mb-0.5">SD</div>
                                    <div className="font-bold text-slate-700">
                                      {stats.overall.sd !== null ? stats.overall.sd.toFixed(config.decimals) : '—'}
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Sex Breakdown */}
                                <div className="flex gap-2">
                                  {stats.male && (
                                    <div className="flex-1 bg-blue-50 rounded p-2">
                                      <div className="text-[10px] font-semibold text-blue-600 mb-1">Male (N={stats.male.n})</div>
                                      <div className="text-xs text-blue-700">
                                        <span className="font-medium">μ:</span> {stats.male.mean.toFixed(config.decimals)}{config.unit}
                                        {stats.male.sd !== null && (
                                          <span className="ml-2"><span className="font-medium">σ:</span> {stats.male.sd.toFixed(config.decimals)}</span>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                  {stats.female && (
                                    <div className="flex-1 bg-pink-50 rounded p-2">
                                      <div className="text-[10px] font-semibold text-pink-600 mb-1">Female (N={stats.female.n})</div>
                                      <div className="text-xs text-pink-700">
                                        <span className="font-medium">μ:</span> {stats.female.mean.toFixed(config.decimals)}{config.unit}
                                        {stats.female.sd !== null && (
                                          <span className="ml-2"><span className="font-medium">σ:</span> {stats.female.sd.toFixed(config.decimals)}</span>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </>
              ) : (
                <div className="text-center py-12">
                  <p className="text-white/70 text-lg">No teams found. Add team assignments to athlete profiles to see team statistics.</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Contact Information */}
        <div className="mt-8 mb-6 text-center" data-testid="contact-section">
          <div className="bg-white/10 backdrop-blur rounded-lg p-6 max-w-md mx-auto">
            <h3 className="text-xl font-semibold text-white mb-4">Contact Us</h3>
            <div className="space-y-2">
              <p className="text-white/90">
                <span className="font-medium">Email:</span>{' '}
                <a href="mailto:jason@armorytraining.com" className="text-blue-300 hover:text-blue-200 underline">
                  jason@armorytraining.com
                </a>
              </p>
              <p className="text-white/90">
                <span className="font-medium">Text:</span>{' '}
                <a href="sms:801-918-4192" className="text-blue-300 hover:text-blue-200 underline">
                  801-918-4192
                </a>
              </p>
            </div>
          </div>
        </div>

        {/* Login Section */}
        <div className="mt-6 mb-6" data-testid="login-section">
          <Card className="max-w-xs mx-auto bg-white/95 backdrop-blur shadow-xl">
            <CardHeader className="text-center border-b py-2">
              <CardTitle className="text-base" data-testid="login-title">Coach & Staff Login</CardTitle>
            </CardHeader>
            <CardContent className="pt-3 pb-3">
              <form onSubmit={handleLogin} className="space-y-3">
                <Input
                  type="password"
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-9 text-center text-sm"
                  autoFocus
                  required
                  data-testid="password-input"
                />
                <Button 
                  type="submit" 
                  className="w-full h-9 text-sm bg-slate-800 hover:bg-slate-700"
                  disabled={loginLoading}
                  data-testid="login-submit-btn"
                >
                  {loginLoading ? "Checking..." : "Login"}
                </Button>
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setShowRecovery(true)}
                    className="text-xs text-slate-600 hover:text-slate-900 hover:underline"
                    data-testid="forgot-password-btn"
                  >
                    Forgot password?
                  </button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-slate-900 text-white/70 text-center py-4">
        <p className="text-sm">© 2026 The Armory - Strength & Speed</p>
      </div>

      {/* Password Recovery Dialog */}
      <Dialog open={showRecovery} onOpenChange={setShowRecovery}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail size={20} />
              Password Recovery
            </DialogTitle>
            <DialogDescription>
              A password recovery email will be sent to the registered email address.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-900">
                <strong>Recovery Email:</strong> kta.jason1@gmail.com
              </p>
              <p className="text-xs text-blue-700 mt-2">
                The password will be sent to this email address.
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowRecovery(false)}
              disabled={recoveryLoading}
            >
              Cancel
            </Button>
            <Button 
              onClick={handlePasswordRecovery}
              disabled={recoveryLoading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {recoveryLoading ? "Sending..." : "Send Recovery Email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PublicLeaderboard;
